import { maskAccount } from "./masking";
import { formatMoney } from "./money";
import type { BuyerFundingReview, GapType, OpenGap } from "./types";

export type PolicyGateStatus = "passed" | "needs_exception" | "blocked" | "not_applicable";

export type PolicyGate = {
  id: string;
  name: string;
  status: PolicyGateStatus;
  requiredEvidence: string[];
  retainedGapIds: string[];
  note: string;
};

function gapIds(review: BuyerFundingReview, ...types: GapType[]): string[] {
  return review.openGaps.filter((gap) => types.includes(gap.gapType)).map((gap) => gap.id);
}

export function buildPolicyGates(review: BuyerFundingReview): PolicyGate[] {
  const inbound = review.inboundReceipt;
  const hasExceptionAction =
    review.officeAction.action === "Recorded exception" ||
    review.openGaps.some((gap) => gap.exceptionRequired);
  const requiredMatchRows = ["amount", "sender_source", "instruction_version", "receiving_account", "reference", "timing"];
  const requiredRowsPresent = requiredMatchRows.every((rowType) =>
    review.matches.some((match) => match.rowType === rowType)
  );
  const sourceRecorded = Boolean(review.sourceAccountEvidence.evidenceType && review.sourceAccountEvidence.evidenceType !== "Unable to check");
  const initiationRecorded = Boolean(
    review.paymentInitiation.evidenceType && !["None", "Not applicable", "Not initiated"].includes(review.paymentInitiation.evidenceType)
  );

  return [
    {
      id: "gate-expected-amount",
      name: "Gate 1 — Expected amount recorded",
      status: review.expectedFunds.amount || review.expectedAmount ? "passed" : "needs_exception",
      requiredEvidence: ["Expected amount", "Funding purpose", "Expected-funds source or manual exception"],
      retainedGapIds: gapIds(review, "Amount mismatch"),
      note: review.expectedFunds.sourceDocumentId
        ? `Source recorded: ${review.expectedFunds.sourceDocumentId}`
        : "Expected amount is manual or source missing."
    },
    {
      id: "gate-active-instructions",
      name: "Gate 2 — Active instructions recorded",
      status:
        review.fundingInstruction.versionLabel && review.fundingInstruction.allowedRails.length
          ? "passed"
          : "needs_exception",
      requiredEvidence: [
        "Instruction version",
        "Delivery channel",
        "Receiving account summary",
        "Allowed rail",
        "Sent or relied-on timestamp"
      ],
      retainedGapIds: gapIds(review, "Instruction mismatch", "Receiving account mismatch", "Rail mismatch"),
      note: review.fundingInstruction.versionLabel
        ? `Active instruction ${review.fundingInstruction.versionLabel} recorded.`
        : "Instruction version missing."
    },
    {
      id: "gate-buyer-acknowledgment",
      name: "Gate 3 — Buyer acknowledgment recorded",
      status:
        review.buyerAcknowledgment.type && review.buyerAcknowledgment.type !== "Not obtained"
          ? "passed"
          : "needs_exception",
      requiredEvidence: ["Acknowledgment channel", "Acknowledged amount", "Instruction version", "Timestamp"],
      retainedGapIds: gapIds(review, "Missing buyer acknowledgment", "Instruction mismatch"),
      note: review.buyerAcknowledgment.type
        ? "Buyer acknowledgment recorded as evidence."
        : "Buyer acknowledgment not recorded."
    },
    {
      id: "gate-source-evidence",
      name: "Gate 4 — Source evidence recorded",
      status: sourceRecorded
        ? review.openGaps.some((gap) =>
            ["Sender/source mismatch", "Manual-only evidence", "Third-party source"].includes(gap.gapType)
          )
          ? "needs_exception"
          : "passed"
        : "needs_exception",
      requiredEvidence: ["Funding party", "Source evidence type", "Limitation"],
      retainedGapIds: gapIds(
        review,
        "Missing source account evidence",
        "Sender/source mismatch",
        "Third-party source",
        "Gift funds missing evidence",
        "Manual-only evidence",
        "Stale evidence"
      ),
      note: sourceRecorded
        ? `${review.sourceAccountEvidence.provider} source evidence recorded.`
        : "No source evidence recorded."
    },
    {
      id: "gate-payment-initiation",
      name: "Gate 5 — Payment initiation recorded",
      status: initiationRecorded ? "passed" : "needs_exception",
      requiredEvidence: ["Initiated amount", "Sender", "Destination", "Trace or reference", "Payment rail"],
      retainedGapIds: gapIds(review, "Missing initiation evidence", "Missing file number / memo"),
      note: review.paymentInitiation.evidenceType || "Payment initiation evidence not recorded."
    },
    {
      id: "gate-inbound-receipt",
      name: "Gate 6 — Inbound receipt recorded",
      status:
        inbound.sourceType !== "not_received" && inbound.amountReceived
          ? review.openGaps.some((gap) =>
              ["Missing inbound receipt", "Ledger missing", "ACH return risk"].includes(gap.gapType)
            )
            ? "needs_exception"
            : "passed"
          : "blocked",
      requiredEvidence: [
        "Amount received",
        "Received timestamp",
        "Receiving account",
        "Sender",
        "Ledger or payment-system source"
      ],
      retainedGapIds: gapIds(review, "Missing inbound receipt", "ACH return risk"),
      note: `Inbound source recorded: ${inbound.sourceType}.`
    },
    {
      id: "gate-four-way-match",
      name: "Gate 7 — Four-way match reviewed",
      status: requiredRowsPresent
        ? review.matches.some((match) => match.result === "Did not match" || match.result === "Open gap retained")
          ? "needs_exception"
          : "passed"
        : "needs_exception",
      requiredEvidence: [
        "Amount row",
        "Source/sender row",
        "Instruction row",
        "Receiving account row",
        "Reference row",
        "Timing row"
      ],
      retainedGapIds: review.openGaps.map((gap) => gap.id),
      note: `${review.matches.length} match rows recorded; ${review.openGaps.length} open gap(s) retained.`
    },
    {
      id: "gate-office-action",
      name: "Gate 8 — Office action recorded",
      status: review.officeAction.action ? (hasExceptionAction ? "needs_exception" : "passed") : "blocked",
      requiredEvidence: [
        "Action",
        "Funds status",
        "Notes for hold/exception/return",
        "Manager or owner sign-off when policy requires"
      ],
      retainedGapIds: review.openGaps.filter((gap) => gap.exceptionRequired).map((gap) => gap.id),
      note: review.officeAction.action || "Office action not recorded."
    },
    {
      id: "gate-signoff",
      name: "Gate 9 — Sign-off recorded",
      status: review.signoff.signedAt ? "passed" : "blocked",
      requiredEvidence: [
        "Reviewer",
        "Role",
        "Office",
        "Authentication method",
        "Gaps acknowledged",
        "Receipt hash"
      ],
      retainedGapIds: [],
      note: review.signoff.signedAt
        ? `Signed by ${review.signoff.signerName || review.createdBy}.`
        : "Reviewer sign-off required before export."
    }
  ];
}

export type IntegrationStatus = {
  name: string;
  mode: "live" | "manual_evidence" | "simulated" | "external_blocked" | "parked";
  reason: string;
};

export function buildIntegrationStatus(): IntegrationStatus[] {
  return [
    {
      name: "Plaid account / balance evidence",
      mode: "external_blocked",
      reason: "Waiting on external Plaid review; manual Plaid evidence rows are supported now."
    },
    {
      name: "CertifID result import",
      mode: "parked",
      reason: "Parked by product decision; recorded as vendor evidence only."
    },
    {
      name: "Closinglock result import",
      mode: "parked",
      reason: "Parked by product decision; recorded as vendor evidence only."
    },
    {
      name: "Bank feed / trust account feed",
      mode: "external_blocked",
      reason: "Trust ledger feeds remain manual; buyer source account can use Plaid when live."
    },
    {
      name: "TPS save-back",
      mode: "manual_evidence",
      reason: "Save-back ZIP + D1/R2 receipt export are live; Qualia/SoftPro API write-back is not wired."
    },
    {
      name: "Persona identity verification",
      mode: "simulated",
      reason:
        "Live when VITE_PERSONA_TEMPLATE_ID plus VITE_PERSONA_ENVIRONMENT_ID (or VITE_PERSONA_ENVIRONMENT) and PERSONA_API_KEY are set."
    },
    {
      name: "Buyer Funding Flow",
      mode: "simulated",
      reason:
        "Secure instruction delivery at /files/{code}; Persona on identity step; Plaid Link when VITE_PLAID_LINK_TOKEN is set."
    },
    {
      name: "Payment rails",
      mode: "manual_evidence",
      reason: "Rails are recorded and matched as evidence; Veto does not move money."
    }
  ];
}

export function buildAccountingReconciliation(review: BuyerFundingReview) {
  const expected = review.expectedFunds.amount || review.expectedAmount;
  const received = review.inboundReceipt.amountReceived;
  const diffCents =
    expected && received ? received.amountCents - expected.amountCents : undefined;
  return {
    source: review.inboundReceipt.sourceType,
    amountExpected: formatMoney(expected),
    amountReceived: formatMoney(received),
    difference:
      diffCents === undefined
        ? "Unable to determine"
        : formatMoney({ amountCents: diffCents, currency: "USD" }),
    senderShown: review.inboundReceipt.senderShown || "Not recorded",
    receivingAccount: maskAccount(review.inboundReceipt.receivingAccountLast4),
    paymentRail: review.inboundReceipt.rail || review.paymentRail.rail,
    reference: review.inboundReceipt.reference || review.inboundReceipt.memo || "Not recorded",
    traceNumber: review.inboundReceipt.traceNumber || "Not recorded",
    ledgerEntry: review.inboundReceipt.trustLedgerEntryId || "Not recorded",
    appliedToFile: review.inboundReceipt.appliedToFile ? "Yes" : "No",
    returnRiskStatus: review.inboundReceipt.returnRiskStatus || "unknown",
    recommendedAccountingState: !received
      ? "not_received"
      : review.inboundReceipt.returnRiskStatus === "open"
        ? "hold_return_risk"
        : diffCents === 0
          ? "match_recorded"
          : diffCents && diffCents > 0
            ? "overage_recorded"
            : diffCents && diffCents < 0
              ? "shortage_recorded"
              : "received_review_required"
  };
}

export function buildManagerExceptionQueue(review: BuyerFundingReview) {
  const exceptionGaps = review.openGaps.filter(
    (gap) => gap.exceptionRequired || gap.severity === "stop_hold"
  );
  return {
    requiresManagerReview:
      exceptionGaps.length > 0 ||
      review.officeAction.managerReviewStatus === "requested" ||
      review.officeAction.managerReviewStatus === "owner_exception_recorded",
    ownerExceptionRequired:
      review.officeAction.managerReviewStatus === "owner_exception_recorded" ||
      exceptionGaps.some((gap) => gap.severity === "stop_hold"),
    fileNumber: review.fileNumber,
    officeAction: review.officeAction.action,
    retainedExceptionGaps: exceptionGaps.map((gap) => ({
      id: gap.id,
      type: gap.gapType,
      severity: gap.severity,
      description: gap.description,
      status: gap.status
    })),
    managerDecisionOptions: [
      "Request more evidence",
      "Hold pending review",
      "Record exception",
      "Return / rejected funds",
      "Accept for file with retained gaps"
    ]
  };
}

export function buildProviderEvidenceSchema(review: BuyerFundingReview) {
  const evidence = review.sourceAccountEvidence;
  return {
    rule: "Vendor outputs are evidence, not final authority.",
    rows: [
      {
        provider: evidence.provider,
        mode:
          evidence.provider === "plaid"
            ? "manual_plaid_evidence_until_external_review_completes"
            : evidence.provider === "certifid" || evidence.provider === "closinglock"
              ? "parked_vendor_evidence"
              : "manual_or_uploaded_evidence",
        resultTimestamp: evidence.recordedAt,
        resultType: "source_account_evidence",
        matchedFields: {
          ownerName: evidence.ownerName,
          ownerMatchResult: evidence.ownerMatchResult,
          accountLast4: evidence.accountLast4,
          balanceRecorded: evidence.balanceRecorded
        },
        limitation: evidence.limitation,
        sourceArtifact: evidence.sourceDocumentId || "source evidence row",
        operatorAcknowledgmentRequired: true
      }
    ],
    parkedProviders: ["certifid", "closinglock"],
    pendingExternalReview: ["plaid"]
  };
}

export function buildAuditPackageManifest(review: BuyerFundingReview) {
  return {
    product: "Buyer Funding Review Receipt",
    reviewNumber: review.reviewNumber,
    version: review.receiptVersion,
    packageArtifacts: [
      "Buyer Funding Review Receipt HTML (print-ready PDF)",
      "Evidence appendix",
      "Source manifest",
      "Event log",
      "Field cross-reference CSV",
      "Hashes",
      "Source packet JSON",
      "Policy gates JSON",
      "Buyer collection packet JSON",
      "Accounting reconciliation JSON",
      "Provider evidence schema JSON",
      "Manager exception queue JSON",
      "Integration status JSON",
      "Buyer portal submissions JSON",
      "Redacted summary JSON",
      "Save-back note"
    ],
    immutableAfterSignoff: Boolean(review.signoff.signedAt),
    redactedExportAvailable: true,
    receiptClaimsMapToSourceRows: true,
    eventLogCount: review.events.length,
    sourceRowCount: review.sourceRows.length,
    buyerPortalSubmissionCount: review.buyerPortalSubmissions?.length || 0
  };
}

export function summarizeOpenGaps(gaps: OpenGap[]): string {
  if (!gaps.length) return "No open gaps retained.";
  return gaps.map((gap) => `${gap.gapType}: ${gap.description}`).join(" ");
}
